import { Button, ButtonContainer, Layout } from '@vwfs-bronson/bronson-react';
import { useFormikContext } from 'formik';
import { cloneDeep } from 'lodash';
import React, { FunctionComponent } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useFlowTypeChecker } from '../../../services/common/miscUtils';
import { store } from '../../../services/redux';
import { useSaveSessionWithContinueLinkMutation } from '../../../services/redux/features/api/saveAndContinue.api-slice';
import { setToken } from '../../../services/redux/features/auth.slice';
import { journeyDataSelectors } from '../../../services/redux/features/journeyData.slice';
import { transactionSlice } from '../../../services/redux/features/transaction.slice';
import { newQuotationSliceActions, WizardPagesEnum } from '../newQuotation.slice';
import { BusinessProspectFormModel } from './BusinessProspectForm/formModel';
import { PrivateProspectFormModel } from './PrivateProspectForm/formModel';

export interface ProspectFormNavigationModel {
  isBusinessTypeUnselected?: boolean;
}

export const ProspectFormNavigation: FunctionComponent<ProspectFormNavigationModel> = ({
  isBusinessTypeUnselected,
}) => {
  const { submitForm, values, isValid } = useFormikContext<PrivateProspectFormModel | BusinessProspectFormModel>();
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const flowTypeIs = useFlowTypeChecker();
  const [saveSessionWithContinueLink, { isLoading }] = useSaveSessionWithContinueLinkMutation();
  const numberOfChosenQuotes = useSelector(journeyDataSelectors.numberOfChosenQuotes);

  const handleBackClick = () => {
    const backPage = flowTypeIs('servicePlan') ? WizardPagesEnum.standaloneServicePlan : WizardPagesEnum.compareQuotes;
    dispatch(newQuotationSliceActions.wizardNavigateToPage(backPage));
  };

  const handleContinueClick = async () => {
    await submitForm();

    const contactNumber = `+${values.countryCode}${values.mobileNumber}`;

    // This prevent to have snc panel opened on snc restore.
    const state = cloneDeep(store.getState());
    state.uiState = {
      ...state.uiState,
      snCSubmitted: true,
      snCClosed: true,
    };

    const response = await saveSessionWithContinueLink({
      receiverEmail: values.email!,
      receiverPhoneNumber: contactNumber,
      frontendModel: state,
    }).unwrap();

    dispatch(setToken(response.token));
    dispatch(transactionSlice.actions.setTransactionId(response.transactionId));

    if (numberOfChosenQuotes === 1) {
      dispatch(newQuotationSliceActions.wizardNavigateToPage(WizardPagesEnum.customerData));
    } else {
      dispatch(newQuotationSliceActions.wizardNavigateToPage(WizardPagesEnum.compareQuotes));
    }
  };

  return (
    <Layout center className="u-mt">
      <ButtonContainer center>
        <Layout.Item default="1/12">
          <i className="c-icon c-icon--[semantic-print] c-icon--medium" />
        </Layout.Item>

        <Layout.Item default="1/4">
          <Button testId="button_goBack" full secondary onClick={handleBackClick}>
            {t(`prospectPage:navigation:back`)}
          </Button>
        </Layout.Item>

        <Layout.Item default="1/4">
          <Button full disabled={isBusinessTypeUnselected || !isValid || isLoading} onClick={handleContinueClick}>
            {t(`prospectPage:navigation:saveAndContinue`)}
          </Button>
        </Layout.Item>
      </ButtonContainer>
    </Layout>
  );
};
